import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import {
  core,
  type ResponseCluster,
  type ResponseClustering,
} from '@mentimeter/http-clients';
import update from 'immutability-helper';
import useSWR, { mutate, useSWRConfig, type SWRConfiguration } from 'swr';
import { useCallback, useRef, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import invariant from 'tiny-invariant';

export const POLLING_INTERVAL = 3000;
export const MAX_POLL_COUNT = 10;

export interface UpdateResponseClusterArguments {
  cluster: ResponseCluster;
}

export type UpdateResponseCluster = (
  props: UpdateResponseClusterArguments,
) => void;

export interface CreateNewClusterArguments {
  clusteringId: string;
  clusterLabel: string;
}

export type CreateNewCluster = (
  props: CreateNewClusterArguments,
) => Promise<ResponseCluster | undefined>;

export interface AssignResponseToClusterArguments {
  updatedTargetCluster: ResponseCluster;
  assignedResponseId: string;
}

export type AssignResponseToCluster = (
  props: AssignResponseToClusterArguments,
) => void;

const clusteringCacheKey = (questionAdminKey: string): string =>
  `/response-clusterings/for-question/${questionAdminKey}`;

export const clusteringTestDataOptions = {
  revalidateIfStale: false,
  revalidateOnMount: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

export const useClusteringsCache = ({
  questionAdminKey,
}: {
  questionAdminKey: string;
}) => {
  const { cache } = useSWRConfig();
  return { data: cache.get(clusteringCacheKey(questionAdminKey))?.data };
};

export const useClustersForQuestion = ({
  slideAdminKey,
  shouldFetch = true,
  options,
  testVotesShowing,
}: {
  slideAdminKey: string;
  shouldFetch?: boolean;
  options?: SWRConfiguration<ResponseClustering>;
  testVotesShowing: boolean;
}) => {
  const hasResetData = useRef(false);
  const [pollCount, setPollCount] = useState(0);
  const hasExistingClusters = useRef(false);

  if (testVotesShowing && !hasResetData.current) {
    mutate(clusteringCacheKey(slideAdminKey), undefined, {
      revalidate: false,
    });
    hasResetData.current = true;
  }
  if (!testVotesShowing) hasResetData.current = false;

  const refreshInterval: SWRConfiguration<ResponseClustering>['refreshInterval'] =
    (data) => {
      const isComplete = data?.status === 'complete';
      const hasReachedMaxPollCount = pollCount >= MAX_POLL_COUNT;

      if (hasReachedMaxPollCount && !isComplete) {
        return 0;
      }

      if (isComplete) {
        // used for tracking only
        hasExistingClusters.current = Boolean(
          pollCount === 0 && data && data.status === 'complete',
        );
      }

      return POLLING_INTERVAL;
    };

  const fetcher = async () => {
    try {
      const { data } = await core().responseClusterings.post({
        question_admin_key: slideAdminKey,
      });
      if (data.status === 'error') {
        throw new Error('Cluster error');
      }

      if (data.status === 'complete') {
        setPollCount(0);
      } else {
        setPollCount((count) => count + 1);
      }

      return data;
    } catch (error) {
      captureException(
        new MentiError('Fetching response clusterings', {
          cause: error,
          feature: 'slides',
        }),
      );
      throw error;
    }
  };

  const {
    data: clusteringForQuestion,
    error: clusteringForQuestionError,
    mutate: triggerRefetch,
    isLoading,
  } = useSWR(clusteringCacheKey(slideAdminKey), shouldFetch ? fetcher : null, {
    refreshInterval,
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    ...options,
  });

  const regenerate = useCallback(async () => {
    setPollCount(0);
    await triggerRefetch();
  }, [triggerRefetch]);

  return {
    regenerate,
    clusteringForQuestion,
    clusteringForQuestionError,
    isLoading:
      isLoading ||
      !(
        clusteringForQuestionError ||
        clusteringForQuestion?.status === 'complete'
      ),
    maxAttemptsReached: pollCount >= MAX_POLL_COUNT,
    hasExistingClusters,
  };
};

const sendAssignResponseToClusterRequest = async (
  _key: string,
  { arg }: { arg: AssignResponseToClusterArguments },
) => {
  const { updatedTargetCluster, assignedResponseId } = arg;

  await core().responseClusterings.assignResponse(updatedTargetCluster.id, {
    response_ids: [assignedResponseId],
  });
};

const sendUpdateResponseClusterRequest = async (
  _key: string,
  { arg }: { arg: UpdateResponseClusterArguments },
) => {
  const { cluster } = arg;
  const { data } = await core().responseClusterings.clusters.put(cluster);
  return data;
};

const sendCreateNewClusterRequest = async (
  _key: string,
  { arg }: { arg: CreateNewClusterArguments },
) => {
  const { clusteringId, clusterLabel } = arg;
  const { data } = await core().responseClusterings.clusters.post(
    clusteringId,
    { label: clusterLabel },
  );
  return data;
};

export const useClusterResponses = (questionAdminKey: string) => {
  const { trigger: assignResponseToCluster, error: assignError } =
    useSWRMutation(
      clusteringCacheKey(questionAdminKey),
      sendAssignResponseToClusterRequest,
      // TODO: set revalidate = false and populateCache = true to not call generate again
    );
  if (assignError)
    captureException(new MentiError(assignError, { feature: 'slides' }));

  const { trigger: updateResponseCluster, error: UpdateResponseClusterError } =
    useSWRMutation(
      clusteringCacheKey(questionAdminKey),
      sendUpdateResponseClusterRequest,
      {
        revalidate: false,
        populateCache: (
          resultData: ResponseCluster,
          current?: ResponseClustering,
        ) => {
          invariant(
            current,
            'useClusterResponses(SWR): current ResponseClustering is required',
          );

          const clusterIndex = current.clusters.findIndex(
            (cluster) => cluster.id === resultData.id,
          );
          if (clusterIndex === -1) return current;

          return update(current, {
            clusters: { [clusterIndex]: { $set: resultData } },
          });
        },
      },
    );

  if (UpdateResponseClusterError)
    captureException(
      new MentiError(UpdateResponseClusterError, { feature: 'slides' }),
    );

  const { trigger: createNewCluster, error: createNewClusterError } =
    useSWRMutation(
      clusteringCacheKey(questionAdminKey),
      sendCreateNewClusterRequest,
    );
  if (createNewClusterError)
    captureException(
      new MentiError(createNewClusterError, { feature: 'slides' }),
    );

  return {
    assignResponseToCluster: assignResponseToCluster as AssignResponseToCluster,
    updateResponseCluster: updateResponseCluster as UpdateResponseCluster,
    createNewCluster: createNewCluster as CreateNewCluster,
    hasError: UpdateResponseClusterError || assignError,
  };
};
